// Catálogo de opciones de `Status` de la base de Notion, en el orden de la especificación.
// Este módulo es TypeScript puro: lo usan las Edge Functions (Deno), la PWA y las pruebas.

export type StatusGroup = 'to_do' | 'in_progress' | 'complete';

export interface StatusOption {
  readonly name: string;
  readonly group: StatusGroup;
}

export const STATUS_OPTIONS: readonly StatusOption[] = [
  { name: 'Not started', group: 'to_do' },
  { name: '🧠 AYUDA MEMORIA TABLERO', group: 'to_do' },
  { name: '⏳ STAND BY', group: 'to_do' },
  { name: '💰 REVISIÓN PRECIOS', group: 'to_do' },
  { name: 'ASAP', group: 'to_do' },
  { name: '🏁 POR HACER', group: 'to_do' },
  { name: '👨‍💻 EN PROGRESO', group: 'in_progress' },
  { name: '🔙 VUELTA CORRECCIÓN', group: 'in_progress' },
  { name: '☑️ ENTREGADO POR PROGRAMACIÓN', group: 'complete' },
  { name: '✅ TERMINADO Y CERRADO', group: 'complete' },
];

export const GROUP_ORDER: readonly StatusGroup[] = ['to_do', 'in_progress', 'complete'];

export const DEFAULT_STATUS = 'Not started';
export const CLOSED_STATUS = '✅ TERMINADO Y CERRADO';

/** Rango que se asigna a un `Status` desconocido: va al final de su grupo. */
export const UNKNOWN_STATUS_RANK = STATUS_OPTIONS.length;

/**
 * Clave de comparación tolerante: Notion puede devolver los emojis con o sin selector de
 * variación (U+FE0F), y con espacios sobrantes. La clave ignora esas diferencias.
 */
export function statusKey(name: string): string {
  return name
    .normalize('NFC')
    .replace(/[\uFE0E\uFE0F]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

const OPTION_BY_KEY = new Map(STATUS_OPTIONS.map((o, i) => [statusKey(o.name), { ...o, rank: i }]));

/** `Status` nulo o vacío se trata como "Not started". */
export function normalizeStatus(name: string | null | undefined): string {
  return name && name.trim() !== '' ? name : DEFAULT_STATUS;
}

export function findStatusOption(
  name: string | null | undefined,
): (StatusOption & { rank: number }) | undefined {
  return OPTION_BY_KEY.get(statusKey(normalizeStatus(name)));
}

export function statusGroup(name: string | null | undefined): StatusGroup {
  return findStatusOption(name)?.group ?? 'to_do';
}

export function statusRank(name: string | null | undefined): number {
  return findStatusOption(name)?.rank ?? UNKNOWN_STATUS_RANK;
}

/** "Cerrada" significa exclusivamente `Status` = "✅ TERMINADO Y CERRADO". */
export function isClosedStatus(name: string | null | undefined): boolean {
  return statusKey(normalizeStatus(name)) === statusKey(CLOSED_STATUS);
}

/**
 * Resuelve un nombre de `Status` contra las opciones reales de la base (por clave tolerante),
 * para escribir en Notion exactamente el nombre que Notion conoce.
 */
export function resolveStatusName(
  requested: string,
  availableNames: readonly string[],
): string | undefined {
  const key = statusKey(requested);
  return availableNames.find((n) => statusKey(n) === key);
}
